/**
 * <pack-builder> — bundle builder on a collection page (templates/collection.bundle.json).
 *
 * State: { lines[], chosen{}, open }. A line is one VARIANT with a quantity —
 * adding the same variant twice increments it; the same product in another
 * size is its own line. `chosen` is each tile's selected size.
 *
 * FILTERS are Horizon's native filters block. A filter change re-fetches this
 * section and morphs it in place (section-renderer.js → morph.js). The parts
 * drawn here (grid, tiers, summary, bar) carry data-skip-subtree-update in the
 * Liquid, so the morph leaves them alone; only the products JSON changes, and
 * an observer on that script redraws the grid. (morph's updatedCallback hook
 * can't be used: it is called on the freshly parsed server node, not on this
 * live element.) `catalog` keeps every product seen, so a bundled product a
 * filter hides still shows in the summary.
 *
 * DISCOUNT — display only. The tiers come from the section settings and must
 * mirror the Shopify automatic discounts set up in admin (e.g. 10% at 2 items,
 * 15% at 3, 20% at 4+). This component never applies a price; checkout does.
 * It only shows what checkout will give, so it errs low on purpose:
 *   - The tier is reached on THIS selection only. The automatic discounts count
 *     every eligible item in the cart, so a shopper with items already in the
 *     cart may get more at checkout than shown here — never less.
 *   - Discount per line = floor(line price × percent) in minor units, summed.
 *     Shopify rounds each allocation to the nearest minor unit, so checkout can
 *     be up to one minor unit per line higher than shown; never lower.
 *
 * TIER BASIS (section setting tier_basis). "items" (default): tiers count items
 * and the percent applies per line, as above. "amount": a shop-wide spend
 * ladder from snippets/spend-tiers.liquid. Tier mins arrive in minor units of
 * the cart currency (none for currencies without thresholds), the tier is reached on the
 * bundle SUBTOTAL, the percent applies to the whole subtotal and is shown as-is
 * (never combined with the compare-at markdown).
 *
 * DIRECT MODE (section setting show_summary off → data-direct): no bundle box.
 * Each card's add button posts its size straight to the cart, no _pack_id.
 *
 * Adding posts real line items to /cart/add.js with a shared `_pack_id`
 * property. Real line items keep per-SKU inventory and product-mix reporting
 * intact; a synthetic bundle SKU would destroy both.
 */

import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';
import { fetchConfig, isMobileBreakpoint, mediaQueryLarge } from '@theme/utilities';

class PackBuilder extends Component {
  connectedCallback() {
    super.connectedCallback();
    if (this.state) return;

    this.strings = this.readJson('[data-pack-strings]') || {};
    const tiers = this.readJson('[data-pack-tiers]') || [];

    // Valid tiers only, ascending by threshold. A tier with 0% is "off".
    this.tiers = tiers
      .filter((t) => t && t.min > 0 && t.percent > 0 && t.percent < 100)
      .sort((a, b) => a.min - b.min);

    this.byAmount = this.dataset.tierBasis === 'amount';
    this.direct = this.hasAttribute('data-direct');
    this.moneyFormat = this.dataset.moneyFormat || '{{amount}}';
    this.currency = this.dataset.currency || window.Shopify?.currency?.active || 'DKK';

    // Free-shipping threshold in minor units of the cart currency; 0 = no nudge.
    this.freeShippingThreshold = Number(this.dataset.freeShippingThreshold) || 0;
    this.upsell = this.readJson('[data-pack-upsell]') || [];

    this.basePercent = 0;
    this.state = { lines: [], chosen: {}, draftQty: {}, open: false };
    this.busy = false;
    this.catalog = new Map();

    // Delegated, so one listener covers buttons the morph or a redraw replaces.
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
    document.addEventListener('submit', this.onQuickAddSubmit, true);
    mediaQueryLarge.addEventListener('change', this.onBreakpoint);

    // A filter/sort change morphs new JSON into this script's text node.
    const productsScript = this.querySelector('[data-pack-products]');
    if (productsScript) {
      this.productsObserver = new MutationObserver(() => this.refresh());
      this.productsObserver.observe(productsScript, { childList: true, characterData: true, subtree: true });
    }

    this.refresh();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.productsObserver?.disconnect();
    document.removeEventListener('submit', this.onQuickAddSubmit, true);
    mediaQueryLarge.removeEventListener('change', this.onBreakpoint);
    if (this.state?.open) document.documentElement.removeAttribute('scroll-lock');
  }

  /** Read the (possibly re-filtered) products, then draw everything. */
  refresh() {
    // Liquid bakes available products only, in the collection's order.
    this.products = this.readJson('[data-pack-products]') || [];
    for (const product of this.products) {
      this.catalog.set(product.id, product);
      // Default each tile to its first sellable variant so a shopper who does
      // not touch the size boxes still adds something buyable.
      if (!this.state.chosen[product.id]) {
        const first = product.variants.find((v) => v.available);
        if (first) this.state.chosen[product.id] = first.id;
      }
      if (!this.state.draftQty[product.id]) this.state.draftQty[product.id] = 1;
    }
    this.basePercent = this.byAmount ? 0 : this.markdownPercent();
    this.renderGrid();
    this.render();
  }

  /** Most common markdown in the catalog (compare-at vs price), in whole %. */
  markdownPercent() {
    const counts = new Map();
    for (const { price, compare_at: compareAt } of this.catalog.values()) {
      if (!(compareAt > price)) continue;
      const percent = Math.round(100 - (price * 100) / compareAt);
      counts.set(percent, (counts.get(percent) || 0) + 1);
    }
    let best = 0;
    let bestCount = 0;
    for (const [percent, count] of counts) if (count > bestCount) [best, bestCount] = [percent, count];
    return best;
  }

  onClick = (event) => {
    const target = event.target.closest('[data-pack-submit], [data-pack-toggle], [data-pack-close], [data-pack-backdrop]');
    if (!target) return;
    if (target.matches('[data-pack-submit]')) this.onSubmit();
    else if (target.matches('[data-pack-toggle]')) this.onToggle();
    else this.closeSheet();
  };

  readJson(selector) {
    const el = this.querySelector(selector);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (error) {
      console.error(`[pack-builder] ${selector} did not parse`, error);
      return null;
    }
  }

  /**
   * Locale string from Liquid `| t`. Liquid filled each {{ var }} with a
   * %var% token (see the data-pack-strings block); fill those here.
   */
  t(key, vars = {}) {
    const template = this.strings[key] ?? key;
    return template.replace(/%(\w+)%/g, (match, name) => (name in vars ? String(vars[name]) : match));
  }

  /** "1 item" / "2 items" — locale has a _singular and a _plural key. */
  plural(key, count) {
    return this.t(count === 1 ? `${key}_singular` : `${key}_plural`, { count });
  }

  /** Customer-facing percent: the tier stacked on the existing markdown. */
  shownPercent(tierPercent) {
    return Math.round(100 - ((100 - this.basePercent) * (100 - tierPercent)) / 100);
  }

  money(cents) {
    return formatMoney(cents, this.moneyFormat, this.currency);
  }

  /** The variant a bundle line refers to, from the catalog. */
  variantOf(line) {
    return this.catalog.get(line.productId)?.variants.find((v) => v.id === line.variantId);
  }

  // --- derived numbers ---------------------------------------------------

  get itemCount() {
    return this.state.lines.reduce((sum, line) => sum + line.qty, 0);
  }

  /** Highest tier reached by `count`, or null. */
  tierFor(count) {
    let reached = null;
    for (const tier of this.tiers) if (count >= tier.min) reached = tier;
    return reached;
  }

  /** First tier not yet reached, or null at the top. */
  nextTierFor(count) {
    return this.tiers.find((tier) => count < tier.min) || null;
  }

  /** Bundle money, plus `measure`: what the tiers are counted in (subtotal or items). */
  totals() {
    const lineTotals = [];
    for (const line of this.state.lines) {
      const variant = this.variantOf(line);
      if (variant) lineTotals.push(variant.price * line.qty);
    }
    const subtotal = lineTotals.reduce((sum, cents) => sum + cents, 0);
    const measure = this.byAmount ? subtotal : this.itemCount;
    const tier = this.tierFor(measure);
    const percent = tier ? tier.percent : 0;
    // Floor so the shown saving is never more than checkout gives: once on the
    // whole bundle for the order discount (amount), per line otherwise.
    const parts = this.byAmount ? [subtotal] : lineTotals;
    const discount = parts.reduce((sum, cents) => sum + Math.floor((cents * percent) / 100), 0);

    return { measure, subtotal, discount, total: subtotal - discount, percent };
  }

  // --- render ------------------------------------------------------------

  /**
   * State changed (size picked, line added/removed). The grid is NOT rebuilt
   * here — only its state is synced — so the button under the pointer and
   * keyboard focus survive the click. renderGrid() runs on boot and filter
   * change only.
   */
  render() {
    this.syncGrid();
    const totals = this.totals();
    this.renderTiers(totals);
    this.renderSummary(totals);
  }

  renderGrid() {
    const el = this.querySelector('[data-pack-grid]');
    if (!el) return;
    // productId → controls that syncGrid() updates without rebuilding cards.
    this.tileRefs = new Map();
    const frag = document.createDocumentFragment();

    if (this.products.length === 0) {
      frag.appendChild(this.metaLine('pack-builder__none', this.t('no_results')));
    }

    for (const product of this.products) {
      // Same title the collection card shows.
      const name = product.title;

      const tile = document.createElement('div');
      tile.className = 'pack-builder__tile';

      const mediaShell = document.createElement('div');
      mediaShell.className = 'pack-builder__media-shell';

      // The image opens the shared quick view. The title stays
      // a normal PDP link for shoppers who want the full product page.
      const gallery = this.querySelector(`[data-pack-gallery="${product.id}"]`);
      if (gallery) mediaShell.appendChild(gallery.content.cloneNode(true));
      else mediaShell.appendChild(this.thumb(product, 'pack-builder__media'));
      mediaShell.addEventListener('click', (event) => {
        if (!event.target.closest('[ref="cardGalleryLink"]') || event.target.closest('.slideshow-control')) return;
        event.preventDefault();
        this.openQuickAdd(product);
      });

      const sizeButtons = new Map();
      if (product.variants.length > 1) {
        const sizes = this.sizePicker(product, sizeButtons);
        sizes.classList.add('pack-builder__sizes--overlay');
        mediaShell.appendChild(sizes);
      }
      tile.appendChild(mediaShell);

      const title = document.createElement('a');
      title.className = 'pack-builder__tile-title';
      title.href = product.url;
      title.textContent = name;

      const meta = document.createElement('span');
      meta.className = 'pack-builder__tile-meta';
      const price = this.metaLine('pack-builder__tile-price', this.money(product.price));
      if (product.compare_at > product.price) {
        const compare = document.createElement('s');
        compare.className = 'pack-builder__tile-compare';
        compare.textContent = this.money(product.compare_at);
        price.appendChild(compare);
      }
      meta.append(title, price);
      tile.appendChild(meta);

      // Quantity + add stay bottom-pinned so action rows align across cards.
      const controls = document.createElement('div');
      controls.className = 'pack-builder__controls';

      const actionRow = document.createElement('div');
      actionRow.className = 'pack-builder__action-row';
      const draftStepper = this.draftStepper(product);

      const add = document.createElement('button');
      add.type = 'button';
      add.className = 'button pack-builder__add';
      add.setAttribute('aria-label', this.t('add_aria', { name }));
      add.addEventListener('click', () => {
        const variantId = this.state.chosen[product.id];
        const quantity = this.state.draftQty[product.id];
        if (this.direct) this.addDirect(product, variantId, quantity, add);
        else this.addVariant(product, variantId, quantity);
      });
      actionRow.append(draftStepper.element, add);
      controls.appendChild(actionRow);
      tile.appendChild(controls);

      this.tileRefs.set(product.id, { sizes: sizeButtons, add, draftQty: draftStepper.qty });
      frag.appendChild(tile);
    }

    el.replaceChildren(frag);
    this.syncGrid();
  }

  /** Selected size + add-button label, updated in place on existing tiles. */
  syncGrid() {
    if (!this.tileRefs) return;
    const qtyByProduct = new Map();
    for (const line of this.state.lines) {
      qtyByProduct.set(line.productId, (qtyByProduct.get(line.productId) || 0) + line.qty);
    }
    for (const [productId, refs] of this.tileRefs) {
      for (const [variantId, button] of refs.sizes) {
        button.setAttribute('aria-checked', String(variantId === this.state.chosen[productId]));
      }
      const inBundle = qtyByProduct.get(productId) || 0;
      if (this.direct) refs.add.textContent = this.t('add_to_cart');
      else refs.add.textContent = inBundle > 0 ? this.t('add_more', { count: inBundle }) : this.t('add');
      refs.draftQty.textContent = String(this.state.draftQty[productId] || 1);
    }
  }

  addVariant(product, variantId, quantity = 1) {
    const variant = product.variants.find((v) => v.id === variantId);
    if (!variant || !variant.available) {
      this.showError(this.t('error_size_sold_out'));
      return;
    }
    this.state.chosen[product.id] = variantId;
    const existing = this.state.lines.find((line) => line.variantId === variantId);
    const qty = Math.max(1, Number(quantity) || 1);
    if (existing) existing.qty += qty;
    else this.state.lines.push({ productId: product.id, variantId, qty });
    this.clearError();
    this.render();
  }

  draftStepper(product) {
    const element = document.createElement('span');
    element.className = 'pack-builder__draft-stepper';
    const qty = this.metaLine('pack-builder__stepper-qty', String(this.state.draftQty[product.id] || 1));
    const change = (delta) => {
      this.state.draftQty[product.id] = Math.max(1, (this.state.draftQty[product.id] || 1) + delta);
      qty.textContent = String(this.state.draftQty[product.id]);
      const tileQty = this.tileRefs?.get(product.id)?.draftQty;
      if (tileQty && tileQty !== qty) tileQty.textContent = qty.textContent;
    };
    element.append(
      this.stepButton('−', this.t('decrease', { name: product.title }), () => change(-1)),
      qty,
      this.stepButton('+', this.t('increase', { name: product.title }), () => change(1))
    );
    return { element, qty };
  }

  sizePicker(product, buttonRefs) {
    const sizes = document.createElement('div');
    sizes.className = 'pack-builder__sizes';
    sizes.setAttribute('role', 'radiogroup');
    sizes.setAttribute('aria-label', this.t('size_for', { name: product.title }));

    for (const variant of product.variants) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'pack-builder__size';
      button.textContent = variant.size;
      button.disabled = !variant.available;
      button.setAttribute('role', 'radio');
      button.setAttribute('aria-checked', String(variant.id === this.state.chosen[product.id]));
      if (!variant.available) {
        button.setAttribute('aria-label', this.t('size_sold_out', { size: variant.size }));
      }
      button.addEventListener('click', () => {
        this.state.chosen[product.id] = variant.id;
        this.clearError();
        this.syncGrid();
      });
      sizes.appendChild(button);
      buttonRefs?.set(variant.id, button);
    }

    return sizes;
  }

  openQuickAdd(product) {
    const proxy = this.querySelector(`[data-pack-quick-add="${product.id}"] quick-add-component`);
    const trigger = proxy?.querySelector('.quick-add__button--choose');
    const dialog = document.getElementById('quick-add-dialog');
    if (!trigger || !dialog) return;
    dialog.dataset.packBuilderId = this.id;
    const modal = dialog.querySelector('dialog');
    modal?.addEventListener('close', () => this.clearQuickAddContext(dialog), { once: true });
    trigger.click();
  }

  clearQuickAddContext(dialog) {
    delete dialog.dataset.packBuilderId;
  }

  onQuickAddSubmit = (event) => {
    const dialog = document.getElementById('quick-add-dialog');
    if (dialog?.dataset.packBuilderId !== this.id) return;
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('#quick-add-dialog')) return;

    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));
    const product = [...this.catalog.values()].find((candidate) =>
      candidate.variants.some((variant) => variant.id === variantId)
    );
    const quantity = Number(formData.get('quantity')) || 1;

    event.preventDefault();
    event.stopImmediatePropagation();
    if (!product) {
      this.showError(this.t('error_unavailable'));
      return;
    }
    this.addVariant(product, variantId, quantity);
    dialog.closeDialog?.();
    this.clearQuickAddContext(dialog);
  };

  renderTiers({ measure: count }) {
    if (this.tiers.length === 0) return;
    const current = this.tierFor(count);
    const next = this.nextTierFor(count);

    const chips = this.refs.tierChips;
    if (chips) {
      chips.replaceChildren();
      for (const tier of this.tiers) {
        const chip = document.createElement('li');
        chip.className = 'pack-builder__tier';
        if (tier === current) {
          chip.classList.add('pack-builder__tier--active');
          chip.setAttribute('aria-current', 'true');
        }
        chip.append(
          ...(this.byAmount
            ? [
                this.metaLine('', this.money(tier.min)),
                this.metaLine('', this.t('tier_percent', { percent: tier.percent })),
              ]
            : [
                this.metaLine('', this.plural('tier_items', tier.min)),
                this.metaLine('', this.t('tier_percent', { percent: this.shownPercent(tier.percent) })),
              ])
        );
        chips.appendChild(chip);
      }
    }

    const top = this.tiers[this.tiers.length - 1];
    if (this.refs.rail) {
      this.refs.rail.setAttribute('aria-valuemax', String(top.min));
      this.refs.rail.setAttribute('aria-valuenow', String(Math.min(count, top.min)));
    }
    const progress = String(Math.min(count / top.min, 1));
    this.refs.railFill?.style.setProperty('--pb-progress', progress);
    this.refs.barRailFill?.style.setProperty('--pb-progress', progress);

    const nextText = !next
      ? ''
      : this.byAmount
        ? this.t('nudge_amount_first', { amount: this.money(next.min - count), percent: next.percent })
        : this.t('nudge_first', { count: next.min - count, percent: this.shownPercent(next.percent) });

    // Mobile bar: the next step of the ladder, or the item count once the top tier is reached.
    if (this.refs.barNudge) this.refs.barNudge.textContent = nextText;

    // Milestones already describe the empty bundle and the reached discount.
    // Only add a prompt when there is a selection and another tier to reach.
    if (this.refs.nudge) this.refs.nudge.textContent = this.itemCount > 0 ? nextText : '';
  }

  renderSummary({ subtotal, discount, total, percent } = this.totals()) {
    const count = this.itemCount;

    if (this.refs.count) this.refs.count.textContent = this.plural('items', count);
    if (this.refs.barCount) this.refs.barCount.textContent = this.plural('items', count);
    if (this.refs.barTotal) this.refs.barTotal.textContent = this.money(total);

    const el = this.refs.lines;
    if (el) {
      el.replaceChildren();
      for (const line of this.state.lines) {
        const row = this.lineRow(line);
        if (row) el.appendChild(row);
      }
    }

    const totalsEl = this.refs.totals;
    if (totalsEl) {
      totalsEl.replaceChildren();
      if (count > 0) {
        totalsEl.appendChild(this.priceLine(this.t('subtotal'), this.money(subtotal)));
        if (discount > 0) {
          totalsEl.appendChild(
            this.priceLine(
              this.t('discount'),
              this.t('discount_value', { amount: this.money(discount), percent })
            )
          );
        }
        totalsEl.appendChild(this.priceLine(this.t('total'), this.money(total), true));
      }
    }

    this.renderShipping(total);

    for (const button of this.refs.submits || []) {
      button.disabled = count === 0 || this.busy;
      if (this.busy) button.textContent = this.t('adding');
      else if (count === 0) button.textContent = this.t('start');
      else button.textContent = this.t('add_to_cart');
    }
  }

  /**
   * Free-shipping nudge + a row of new-season picks (links to the PDP only).
   * Shown while the bundle total after the tier discount is under the
   * threshold; hidden when there is no threshold, no upsell products, or an
   * empty bundle.
   */
  renderShipping(total) {
    const el = this.refs.shipping;
    if (!el) return;
    // Liquid bakes no upsell when there is no threshold, so an empty list is the off switch.
    const remaining = this.freeShippingThreshold - total;
    el.hidden = !(total > 0 && remaining > 0 && this.upsell.length > 0);
    if (el.hidden) return;

    // The cards never change: build them once, then only update the amount.
    if (!this.shipText) {
      this.shipText = this.metaLine('pack-builder__ship-text', '');
      const list = document.createElement('div');
      list.className = 'pack-builder__upsell';
      for (const product of this.upsell) {
        const card = document.createElement('a');
        card.className = 'pack-builder__upsell-card';
        card.href = product.url;
        card.append(
          this.thumb(product, 'pack-builder__media'),
          this.metaLine('pack-builder__upsell-title', product.title),
          this.metaLine('pack-builder__upsell-price', this.money(product.price))
        );
        list.appendChild(card);
      }
      el.append(this.shipText, list);
    }
    this.shipText.textContent = this.t('shipping_nudge', { amount: this.money(remaining) });
  }

  lineRow(line) {
    const product = this.catalog.get(line.productId);
    const variant = this.variantOf(line);
    if (!product || !variant) return null;
    const name = product.title;

    const row = document.createElement('div');
    row.className = 'pack-builder__slot';
    row.appendChild(this.thumb(product, 'pack-builder__slot-sw'));

    const body = document.createElement('span');
    body.className = 'pack-builder__slot-body';
    body.appendChild(this.metaLine('pack-builder__slot-name', name));
    if (product.variants.length > 1) {
      body.appendChild(this.metaLine('pack-builder__slot-size', variant.size));
    }

    const stepper = document.createElement('span');
    stepper.className = 'pack-builder__stepper';
    const minus = this.stepButton('−', this.t('decrease', { name }), () => this.changeQty(line, -1));
    const qty = this.metaLine('pack-builder__stepper-qty', String(line.qty));
    qty.setAttribute('aria-live', 'polite');
    const plus = this.stepButton('+', this.t('increase', { name }), () => this.changeQty(line, 1));
    stepper.append(minus, qty, plus);
    body.appendChild(stepper);
    row.appendChild(body);

    const side = document.createElement('span');
    side.className = 'pack-builder__slot-side';
    side.appendChild(this.metaLine('pack-builder__slot-price', this.money(variant.price * line.qty)));
    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'pack-builder__slot-remove';
    remove.textContent = '×';
    remove.setAttribute('aria-label', this.t('remove', { name }));
    remove.addEventListener('click', () => this.changeQty(line, -line.qty));
    side.appendChild(remove);
    row.appendChild(side);

    return row;
  }

  stepButton(label, ariaLabel, onClick) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'pack-builder__step-btn';
    button.textContent = label;
    button.setAttribute('aria-label', ariaLabel);
    button.addEventListener('click', onClick);
    return button;
  }

  changeQty(line, delta) {
    line.qty += delta;
    if (line.qty <= 0) this.state.lines = this.state.lines.filter((l) => l !== line);
    this.clearError();
    this.render();
    // The row the focused button lived in was re-rendered; keep focus in the
    // panel rather than dropping it to <body>.
    if (this.state.open && !this.refs.summary?.contains(document.activeElement)) {
      this.refs.close?.focus();
    }
  }

  /** Product image, or an empty media box when the product has none. */
  thumb(product, className) {
    const wrap = document.createElement('span');
    wrap.className = className;
    if (!product.image) return wrap;

    const img = document.createElement('img');
    img.src = product.image;
    img.alt = '';
    img.loading = 'lazy';
    wrap.appendChild(img);

    return wrap;
  }

  /** One span of text. textContent only — never innerHTML. */
  metaLine(className, text) {
    const span = document.createElement('span');
    span.className = className;
    span.textContent = text;
    return span;
  }

  priceLine(label, value, isTotal) {
    const line = document.createElement('div');
    line.className = 'pack-builder__pline' + (isTotal ? ' pack-builder__pline--total' : '');
    line.append(this.metaLine('', label), this.metaLine('', value));
    return line;
  }

  showError(message) {
    if (this.refs.error) this.refs.error.textContent = message;
  }

  clearError() {
    this.showError('');
  }

  // --- mobile bottom sheet -----------------------------------------------

  onToggle = () => {
    if (this.state.open) this.closeSheet();
    else this.openSheet();
  };

  openSheet = () => {
    if (!isMobileBreakpoint()) return;
    this.state.open = true;
    this.toggleAttribute('data-sheet-open', true);
    this.refs.toggle?.setAttribute('aria-expanded', 'true');
    // The theme's scroll lock (base.css html[scroll-lock]), same as dialog.js.
    document.documentElement.setAttribute('scroll-lock', '');
    // Wait a frame so the sheet is visible (visibility transition) before focusing.
    requestAnimationFrame(() => this.refs.close?.focus());
  };

  closeSheet = () => {
    if (!this.state.open) return;
    this.state.open = false;
    this.toggleAttribute('data-sheet-open', false);
    this.refs.toggle?.setAttribute('aria-expanded', 'false');
    document.documentElement.removeAttribute('scroll-lock');
    this.refs.toggle?.focus();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape' && this.state.open) {
      event.stopPropagation();
      this.closeSheet();
    }
  };

  onBreakpoint = () => {
    if (!isMobileBreakpoint() && this.state.open) this.closeSheet();
  };

  // --- add to cart -------------------------------------------------------

  onSubmit = async () => {
    if (this.busy || this.state.lines.length === 0) return;

    // One bundle id shared across the line items. Keeps the bundle
    // attributable in reporting without inventing a composite SKU.
    const packId = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
    const items = [];

    for (const line of this.state.lines) {
      const variant = this.variantOf(line);
      if (!variant || !variant.available) {
        this.showError(this.t('error_unavailable'));
        return;
      }
      items.push({ id: variant.id, quantity: line.qty, properties: { _pack_id: packId } });
    }

    this.busy = true;
    this.clearError();
    this.renderSummary();

    try {
      if (!(await this.postItems(items))) return;
      this.state.lines = [];
      if (this.state.open) this.closeSheet();
      this.render();
    } finally {
      this.busy = false;
      this.renderSummary();
    }
  };

  /** Direct mode: one card's size straight to the cart. */
  async addDirect(product, variantId, quantity, button) {
    const variant = product.variants.find((v) => v.id === variantId);
    if (!variant || !variant.available) {
      this.showError(this.t('error_size_sold_out'));
      return;
    }
    if (this.busy) return;
    this.busy = true;
    button.disabled = true;
    button.textContent = this.t('adding');
    try {
      await this.postItems([{ id: variant.id, quantity: Math.max(1, Number(quantity) || 1) }]);
    } finally {
      this.busy = false;
      button.disabled = false;
      this.syncGrid();
    }
  }

  /** POST items to the cart and announce it. Returns false (error shown) on failure. */
  async postItems(items) {
    const root = window.Shopify?.routes?.root || '/';
    try {
      const response = await fetch(`${root}cart/add.js`, fetchConfig('json', { body: JSON.stringify({ items }) }));

      if (!response.ok) {
        // 422 means a variant went out of stock (or below the requested qty)
        // between render and add. The baked JSON is stale by definition.
        const body = await response.json().catch(() => ({}));
        this.showError(body.description || this.t('error_sold_out'));
        return false;
      }

      const cart = await fetch(`${root}cart.js`).then((r) => r.json());

      // CartAddEvent fires as 'cart:update' — cart-drawer.js opens on it (when
      // auto-open is on); cart-icon, header-actions and cart-items refresh.
      document.dispatchEvent(
        new CartAddEvent(cart, this.dataset.sectionId || 'pack-builder', {
          source: 'pack-builder',
          itemCount: items.reduce((sum, item) => sum + item.quantity, 0),
        })
      );
      return true;
    } catch (error) {
      console.error('[pack-builder] add to cart failed', error);
      this.showError(this.t('error_generic'));
      return false;
    }
  }
}

if (!customElements.get('pack-builder')) {
  customElements.define('pack-builder', PackBuilder);
}
